import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { cycleSort, parseSort, serializeSort, type SortState } from './listSort';

/**
 * Puts a list's sort, search and filters in the address bar.
 *
 * A view someone has set up is worth keeping. Reloading the page, following a
 * link a colleague sent, or pressing Back after opening a record should all land
 * on the list as it was, and today none of them do: every filter in this app
 * except the register's date lives in component state and evaporates.
 *
 * ## Absent means default
 *
 * Nothing is written on mount and no parameter has an explicit "off" value, so
 * an untouched page has a completely clean address and clearing a control
 * removes its parameter rather than writing a "no filter" the next reader has to
 * interpret. It also means this never fights the Back button by rewriting the
 * URL behind the reader.
 *
 * A parameter naming a column that does not exist resolves to the default and is
 * LEFT in the address. Correcting somebody's typo by rewriting history buys
 * nothing and costs them their Back button.
 *
 * ## Sorting pushes, typing replaces
 *
 * Clicking a column is a deliberate act, so Back should undo it. Typing is not:
 * twelve keystrokes must not become twelve history entries to walk back
 * through, so the search parameter replaces, and Back from a search leaves the
 * page.
 *
 * ## The search box is ahead of the address
 *
 * `draft` is what is in the box and updates on every keystroke; `query` is what
 * reached the URL, a quarter second after the typing stops. Pages derive their
 * query keys from `query`, so a server-backed search is debounced as a
 * consequence of being addressable rather than as a separate feature.
 */

export interface ListView {
  /** The active sort, or null for the order the rows arrived in. */
  sort: SortState | null;
  /** Advance a column through the cycle. */
  onSort: (id: string) => void;
  /** What reached the address: what to filter and to build query keys from. */
  query: string;
  /** What is in the box right now. Ahead of `query` while typing. */
  draft: string;
  onQuery: (value: string) => void;
  /** Read one other parameter, e.g. a facet or a date. */
  get: (key: string) => string;
  /** Write other parameters. Pass undefined to remove one. */
  set: (patch: Record<string, string | undefined>) => void;
}

/** A parameter is whatever somebody typed into the address bar. */
const read = (search: Record<string, unknown>, key: string): string =>
  typeof search[key] === 'string' ? search[key] : '';

export function useListView({
  search,
  columns,
  defaultSort,
  prefix,
  debounce = 250,
}: {
  /** The route's own typed search object, read by the page. */
  search: Record<string, unknown>;
  /**
   * The column list itself, not a second array of ids. Structural rather than
   * a `LedgerColumn` import so this module stays free of the component layer,
   * and so there is no derived list of keys that can fall out of step with the
   * columns it was derived from.
   */
  columns: readonly { id: string; sort?: unknown; numeric?: boolean }[];
  /** Where the list sits with no explicit sort. Omit for arrival order. */
  defaultSort?: SortState;
  /**
   * Namespaces this list's own two parameters, so `sort` becomes
   * `${prefix}_sort` and `q` becomes `${prefix}_q`. Needed as soon as one route
   * carries two lists, because both would otherwise answer to the same `sort`.
   *
   * It deliberately does NOT reach `get` and `set`. Those read the page's own
   * parameters, a date range or a filter, which belong to the page rather than
   * to either of its lists: a range narrows what was fetched, and both lists
   * then show what came back.
   */
  prefix?: string;
  debounce?: number;
}): ListView {
  const navigate = useNavigate();
  // Plain strings rather than a helper, so they sit in dependency arrays by
  // value instead of as a function rebuilt on every render.
  const sortParam = prefix ? `${prefix}_sort` : 'sort';
  const queryParam = prefix ? `${prefix}_q` : 'q';

  const sortable = useMemo(
    () => columns.filter((column) => column.sort !== undefined).map((column) => column.id),
    [columns],
  );

  const raw = read(search, sortParam);
  const sort = parseSort(raw, sortable) ?? defaultSort ?? null;

  const set = useCallback(
    (patch: Record<string, string | undefined>, replace = false) => {
      void navigate({
        /*
         * Cast because this hook is deliberately route agnostic. `useNavigate`
         * with no `to` types its search reducer against every route at once,
         * and that union collapses to `never`. The page that owns the route has
         * already declared these parameters in its own `validateSearch`, which
         * is where the checking belongs.
         */
        search: ((prev: Record<string, unknown>) => ({ ...prev, ...patch })) as never,
        replace,
      });
    },
    [navigate],
  );

  const onSort = useCallback(
    (id: string) => {
      const column = columns.find((c) => c.id === id);
      if (!column) return;
      /*
       * Cycle the RESOLVED sort, never the parsed one. The two agree on every
       * legible parameter, which is what makes the difference easy to miss, and
       * part company only on an unusable one, where the header is showing the
       * default while the parsed value is null. Cycling null there would restart
       * the column already marked as sorted, so the first click would do nothing
       * anyone could see.
       */
      const next = cycleSort(sort, id, first(column));
      set({ [sortParam]: serializeSort(next) ?? undefined });
    },
    [columns, sort, set, sortParam],
  );

  // What is in the box, seeded from the address so a shared link fills it in.
  const url = read(search, queryParam);
  const [draft, setDraft] = useState(url);
  const [written, setWritten] = useState(url);

  /*
   * Adjusting state during render rather than in an effect, which is React's
   * own answer to "reset state when a prop changes" and saves the extra pass an
   * effect would cost.
   *
   * `written` is what distinguishes our own commit from somebody pressing Back.
   * Without it, the address catching up to a keystroke would look identical to
   * an external change, and adopting it would throw away whatever was typed
   * while the navigation was in flight.
   */
  if (url !== written) {
    setWritten(url);
    setDraft(url);
  }

  useEffect(() => {
    if (draft === url) return;
    const timer = window.setTimeout(() => {
      setWritten(draft);
      set({ [queryParam]: draft || undefined }, true);
    }, debounce);
    return () => window.clearTimeout(timer);
  }, [draft, url, debounce, set, queryParam]);

  return {
    sort,
    onSort,
    query: url,
    draft,
    onQuery: setDraft,
    get: (key) => read(search, key),
    set: (patch) => set(patch),
  };
}

/**
 * Which way a column opens.
 *
 * A count, a rate or a date is asked "most" or "newest" first, so it opens
 * descending. A name is asked A to Z. Flattening this to one answer would make
 * a first click on Name sort Z to A.
 */
function first(column: { numeric?: boolean }): 'asc' | 'desc' {
  return column.numeric ? 'desc' : 'asc';
}
